import React from 'react';
import {
  CharacterData,
  ComputedStats,
  getBrawlerData
} from '../../types/character';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import Slot from '../Common/Slot';
import Button from '../Common/Button';
import Card from '../Common/Card';
import './Focus.css';

interface FocusProps {
  enhancedCharacter: EnhancedCharacter;
}

const Focus: React.FC<FocusProps> = ({ enhancedCharacter }) => {
  if (!enhancedCharacter.character) {
    return (
      <Card title="Focus">
        <div className="focus">
          <div className="focus__loading">
            Loading focus tokens...
          </div>
        </div>
      </Card>
    );
  }

  const character = enhancedCharacter.character;
  
  // Focus tokens are only for Brawler class
  if (character.class?.toLowerCase() !== 'brawler') {
    return null; // Don't render for non-Brawler characters
  }
  
  // Get Brawler-specific data (focus tokens are Brawler-specific)
  const brawlerData = getBrawlerData(character);
  const currentFocus = brawlerData.focusTokens;
  const maxFocus = brawlerData.maxFocusTokens;

  const handleSlotClick = (index: number) => {
    // Mark all up to clicked index
    const newFocus = index + 1;
    if (enhancedCharacter.updateClassSpecificData) {
      enhancedCharacter.updateClassSpecificData({
        brawler: {
          ...getBrawlerData(character),
          focusTokens: Math.min(newFocus, maxFocus)
        }
      });
    }
  };

  const clearFocus = () => {
    if (enhancedCharacter.updateClassSpecificData) {
      enhancedCharacter.updateClassSpecificData({
        brawler: {
          ...getBrawlerData(character),
          focusTokens: 0
        }
      });
    }
  };

  const handleMaxFocusChange = (newMax: number) => {
    if (enhancedCharacter.updateClassSpecificData) {
      enhancedCharacter.updateClassSpecificData({
        brawler: {
          ...getBrawlerData(character),
          maxFocusTokens: Math.max(0, newMax),
          focusTokens: Math.min(currentFocus, newMax)
        }
      });
    }
  };

  return (
    <Card title="Focus">
      <div className="focus">
        <div className="focus__slots">
          {Array.from({ length: Math.max(maxFocus, 6) }, (_, index) => (
            <Slot
              key={index}
              index={index}
              marked={index < currentFocus}
              onClick={() => handleSlotClick(index)}
              type="FOCUS"
              icon="🔮"
              disabled={index >= maxFocus}
            />
          ))}
          <Button
            variant="CLEAR"
            size="SMALL"
            onClick={clearFocus}
            className="clear-button"
          >
            🗑️
          </Button>
        </div>
        
        <div className="focus__info">
          <div className="focus__current">
            Focus: {currentFocus}/{maxFocus}
          </div>
          
          <div className="focus__instruct">
            Roll {character.baseStats.traits?.instinct || 0}d6 during rest, set max to highest roll
          </div>
        </div>
        
        <div className="focus__actions">
          <Button
            variant="SECONDARY"
            size="SMALL"
            onClick={() => handleMaxFocusChange(maxFocus + 1)}
          >
            + Max
          </Button>
          <Button
            variant="SECONDARY"
            size="SMALL"
            onClick={() => handleMaxFocusChange(maxFocus - 1)}
            disabled={maxFocus <= 0}
          >
            - Max
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default Focus;